import { useCallback, useEffect, useRef, useState } from 'react';

import { connectivityBridge } from '@/tauri/offline-recording';
import { logger } from '@/utils/logger/logger';

/**
 * In-flight feedback window for a manual re-probe. The Rust monitor's connect /
 * internet probe timeouts are ~3s, so a still-unreachable re-probe resolves
 * inside this window. We can't end the spinner on the connectivity event alone:
 * the monitor only emits on a *changed* state, so a still-offline re-probe is
 * silent — a successful reconnect instead unmounts the banner entirely.
 */
const REFRESH_FEEDBACK_MS = 3000;

interface UseConnectivityRefresh {
  isRefreshing: boolean;
  refresh: () => void;
}

/**
 * Drives the offline banner's "Refresh" action: triggers an immediate Rust
 * re-probe via {@link connectivityBridge.notifyNetworkChange} and exposes a
 * short `isRefreshing` flag so the button can show in-flight feedback.
 */
export function useConnectivityRefresh(): UseConnectivityRefresh {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const refresh = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsRefreshing(true);

    connectivityBridge.notifyNetworkChange().catch((error) => {
      logger.warn({
        msg: 'Failed to trigger offline connectivity re-probe',
        error: error instanceof Error ? error.message : String(error),
      });
    });

    timeoutRef.current = setTimeout(() => {
      setIsRefreshing(false);
      timeoutRef.current = null;
    }, REFRESH_FEEDBACK_MS);
  }, []);

  return { isRefreshing, refresh };
}
